import { combineReducers, configureStore } from "@reduxjs/toolkit";
import authReducer from "./slices/authSlice";
// eventReducer удален, будет создан заново
import notificationsReducer from "./slices/notificationsSlice";
import uiReducer from "./slices/uiSlice";
import personalizationReducer from "./personalizationSlice";
import collaborationReducer from "./collaborationSlice";
import mobileReducer from "./mobileSlice";
import analyticsReducer from "./analyticsSlice";
import mlAnalyticsReducer from "./mlAnalyticsSlice";
import participantsAnalyticsReducer from "./participantsAnalyticsSlice";
import eventsAnalyticsReducer from "./eventsAnalyticsSlice";
import conversionsAnalyticsReducer from "./conversionsAnalyticsSlice";
import reportsReducer from "./reportsSlice";
import websocketReducer from "./websocketSlice";
import subscriptionReducer from "./slices/subscriptionSlice";
import referralReducer from "./slices/referralSlice";
import ucBalanceReducer from "./slices/ucBalanceSlice";
import { sessionReset } from "./session-reset";

// Реэкспорт типа для использования в компонентах
export type { AuthState } from "./slices/authSlice";

const appReducer = combineReducers({
  // events: eventReducer, // будет создан заново
  ui: uiReducer,
  auth: authReducer,
  notifications: notificationsReducer,
  personalization: personalizationReducer,
  collaboration: collaborationReducer,
  mobile: mobileReducer,
  analytics: analyticsReducer,
  mlAnalytics: mlAnalyticsReducer,
  participants: participantsAnalyticsReducer,
  events: eventsAnalyticsReducer,
  conversions: conversionsAnalyticsReducer,
  reports: reportsReducer,
  websocket: websocketReducer,
  subscription: subscriptionReducer,
  referral: referralReducer,
  ucBalance: ucBalanceReducer,
});

// AD-608. `sessionReset` (конец сессии пользователя в вкладке, шлёт `SessionCacheReset`)
// возвращает к начальному состоянию все срезы, кроме настроек интерфейса и данных
// устройства — они принадлежат вкладке, а не пользователю.
const rootReducer: typeof appReducer = (state, action) =>
  appReducer(
    sessionReset.match(action) && state
      ? { ui: state.ui, mobile: state.mobile }
      : state,
    action,
  );

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Игнорируем некоторые типы действий (например, с объектами Date)
        ignoredActions: [
          "events/fetchEvents/fulfilled",
          "events/fetchEventById/fulfilled",
          "notifications/fetchNotifications/fulfilled",
          "notifications/addNotificationFromSocket",
          "notifications/setConnectionStatus",
          "personalization/loadSettings/fulfilled",
          "personalization/saveSettings/fulfilled",
          "personalization/loadTemplates/fulfilled",
          "collaboration/startSession/fulfilled",
          "collaboration/loadComments/fulfilled",
          "collaboration/loadTeamTemplates/fulfilled",
          "collaboration/shareResource/fulfilled",
          "mobile/detectDevice/fulfilled",
          "mobile/loadLayouts/fulfilled",
          "mobile/installPWA/fulfilled",
          "mobile/sendPushNotification/fulfilled",
          "mobile/measurePerformance/fulfilled",
          "analytics/fetchDashboards/fulfilled",
          "analytics/createDashboard/fulfilled",
          "analytics/fetchWidgets/fulfilled",
          "analytics/createWidget/fulfilled",
          "analytics/trainMLModel/fulfilled",
          "analytics/fetchMetrics/fulfilled",
          "analytics/generateReport/fulfilled",
          "analytics/setDateRange",
        ],
        // Игнорируем определенные пути в состоянии
        ignoredPaths: [
          "events.events",
          "events.selectedEvent",
          "notifications.notifications",
          "notifications.lastConnected",
          "notifications.cache.lastFetch",
          "personalization.settings",
          "personalization.widgets",
          "personalization.kpis",
          "personalization.filters",
          "personalization.layouts",
          "personalization.templates",
          "collaboration.currentSession",
          "collaboration.comments",
          "collaboration.teamTemplates",
          "collaboration.realtimeActivities",
          "collaboration.accessControls",
          "collaboration.versions",
          "mobile.mobileLayouts",
          "mobile.lastTouch",
          "mobile.viewport",
          "mobile.pwaSettings",
          "analytics.dashboards",
          "analytics.currentDashboard",
          "analytics.widgets",
          "analytics.mlModels",
          "analytics.predictiveModels",
          "analytics.dateRange",
          "analytics.metrics",
          "analytics.userBehavior",
          "analytics.realTimeData",
        ],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
